/**
 * @fileoverview Development-only logger utility
 * @module utils/logger
 * 
 * @description
 * Provides logging functions that only output in development mode.
 * In production, these calls are no-ops or can be connected to error tracking services.
 * 
 * @example
 * import { logger } from './utils/logger.js';
 * 
 * logger.log('Debug info');  // Only in dev
 * logger.warn('Warning');    // Only in dev
 * logger.error('Error', err); // Only in dev
 * logger.trackError('component-name', error, { context: 'info' }); // Can send to tracking service
 */

/**
 * Check if running in development mode
 * @returns {boolean} True if in development mode
 */
const isDev = () => {
  // Check NODE_ENV for build-time detection
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) {
    return process.env.NODE_ENV === 'development';
  }
  
  // Check hostname for runtime detection
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || 
           hostname === '127.0.0.1' || 
           hostname.startsWith('192.168.') ||
           hostname.endsWith('.local');
  }
  
  // Default to false (production-safe)
  return false;
};

/**
 * Logger utility with dev-only output
 */
export const logger = {
  /**
   * Log informational message (dev only)
   * @param {...any} args - Arguments to log
   */
  log: (...args) => {
    if (isDev()) {
      console.log(...args);
    }
  },
  
  /**
   * Log warning message (dev only)
   * @param {...any} args - Arguments to log
   */
  warn: (...args) => {
    if (isDev()) {
      console.warn(...args);
    }
  },
  
  /**
   * Log error message (dev only)
   * @param {...any} args - Arguments to log
   */
  error: (...args) => {
    if (isDev()) {
      console.error(...args);
    }
  },
  
  /**
   * Track error for production monitoring
   * In development: logs to console
   * In production: can send to error tracking service (Sentry, LogRocket, etc.)
   * 
   * @param {string} component - Component name where error occurred
   * @param {Error} error - The error object
   * @param {Object} [context={}] - Additional context information
   */
  trackError: (component, error, context = {}) => {
    if (isDev()) {
      console.error(`[${component}]`, error, context);
    } else {
      // In production, send to error tracking service
      // Example: Sentry integration
      if (typeof window !== 'undefined' && window.errorTracker) {
        window.errorTracker.captureException(error, {
          tags: { component },
          extra: context
        });
      }
    }
  },
  
  /**
   * Assert condition and log error if false (dev only)
   * @param {boolean} condition - Condition to assert
   * @param {string} message - Message to display if assertion fails
   */
  assert: (condition, message) => {
    if (isDev() && !condition) {
      console.assert(condition, message);
    }
  },
  
  /**
   * Create a scoped logger for a specific component
   * @param {string} componentName - Name of the component
   * @returns {Object} Scoped logger functions
   */
  scope: (componentName) => {
    return {
      log: (...args) => logger.log(`[${componentName}]`, ...args),
      warn: (...args) => logger.warn(`[${componentName}]`, ...args),
      error: (...args) => logger.error(`[${componentName}]`, ...args),
      trackError: (error, context) => logger.trackError(componentName, error, context),
      assert: (condition, message) => logger.assert(condition, `[${componentName}] ${message}`)
    };
  }
};

/**
 * Check if logging is enabled (for use in conditional logic)
 * @returns {boolean} True if in development mode
 */
export const isLoggingEnabled = isDev;
